import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Mail, Phone, MapPin } from "lucide-react";
import { toast } from "sonner";

export default function Contact() {
  const [settings, setSettings] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", requirements: "", notes: "" });

  useEffect(() => { api.get("/settings").then((r) => setSettings(r.data)); }, []);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/leads", { inquiry_type: "contact", ...form });
      setForm({ name: "", email: "", phone: "", company: "", requirements: "", notes: "" });
      toast.success("Message received. We'll be in touch soon.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <section className="bg-white border-b border-msr-line">
        <div className="msr-container py-16 grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-7">
            <div className="overline mb-3">Contact MSR</div>
            <h1 className="font-heading font-extrabold text-4xl lg:text-6xl tracking-tight" data-testid="contact-title">
              Let's talk about your<br /> technology roadmap.
            </h1>
          </div>
          <p className="lg:col-span-5 text-msr-slate">Reach out for consultations, demos, hardware procurement, or to discuss a custom system. We respond within 3 working days.</p>
        </div>
      </section>

      <section className="bg-msr-surface">
        <div className="msr-container py-12 grid lg:grid-cols-12 gap-8">
          <div className="lg:col-span-5 space-y-4">
            <InfoCard icon={Phone} label="Call" value={settings?.phone} testId="contact-info-phone" />
            <InfoCard icon={Mail} label="Email" value={settings?.email} testId="contact-info-email" />
            <InfoCard icon={MapPin} label="Office" value={settings?.address} testId="contact-info-address" />

            <div className="msr-card mt-2">
              <div className="overline mb-2">Office Hours</div>
              <ul className="text-sm text-msr-slate space-y-1">
                <li className="flex justify-between"><span>Mon – Fri</span><span className="font-semibold text-msr-ink">8:30 AM – 6:00 PM</span></li>
                <li className="flex justify-between"><span>Saturday</span><span className="font-semibold text-msr-ink">9:00 AM – 1:00 PM</span></li>
                <li className="flex justify-between"><span>Sunday</span><span className="font-semibold text-msr-ink">Closed</span></li>
              </ul>
            </div>
          </div>

          <div className="lg:col-span-7">
            <form onSubmit={submit} className="msr-card grid sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2 overline">Send a message</div>
              <Field label="Full Name *" value={form.name} onChange={(v) => update("name", v)} required testId="contact-name-input" />
              <Field label="Email *" type="email" value={form.email} onChange={(v) => update("email", v)} required testId="contact-email-input" />
              <Field label="Phone" value={form.phone} onChange={(v) => update("phone", v)} testId="contact-phone-input" />
              <Field label="Company" value={form.company} onChange={(v) => update("company", v)} testId="contact-company-input" />
              <div className="sm:col-span-2">
                <TextArea label="How can we help? *" value={form.requirements} onChange={(v) => update("requirements", v)} required rows={4} testId="contact-message-input" />
              </div>
              <button type="submit" disabled={submitting} className="sm:col-span-2 btn-msr-primary disabled:opacity-60" data-testid="contact-submit-btn">
                {submitting ? "Sending..." : "Send Message"}
              </button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}

function InfoCard({ icon: Icon, label, value, testId }) {
  return (
    <div className="msr-card flex items-start gap-4" data-testid={testId}>
      <div className="h-10 w-10 grid place-items-center bg-msr-blue text-white flex-shrink-0"><Icon size={16} /></div>
      <div>
        <div className="overline">{label}</div>
        <div className="font-heading font-bold text-msr-ink">{value || "—"}</div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", required, testId }) {
  return (
    <label className="block text-xs font-semibold uppercase tracking-wider text-msr-slate">
      {label}
      <input type={type} required={required} value={value} onChange={(e) => onChange(e.target.value)} className="mt-1 block w-full border border-msr-line bg-white px-3 py-2.5 text-sm text-msr-ink font-normal normal-case tracking-normal focus:outline-none focus:border-msr-blue" data-testid={testId} />
    </label>
  );
}

function TextArea({ label, value, onChange, required, rows = 4, testId }) {
  return (
    <label className="block text-xs font-semibold uppercase tracking-wider text-msr-slate">
      {label}
      <textarea rows={rows} required={required} value={value} onChange={(e) => onChange(e.target.value)} className="mt-1 block w-full border border-msr-line bg-white px-3 py-2.5 text-sm text-msr-ink font-normal normal-case tracking-normal focus:outline-none focus:border-msr-blue resize-none" data-testid={testId} />
    </label>
  );
}
